// Team-centric admin: list of teams (per-team storage/quota) → drill into members + team shares.
// Cookie-session (W2a). Visual styling in admin.css. Localized via i18n. textContent-only.
import type { TeamRow, TeamDetail } from '../admin.js';
import { t } from './i18n.js';

export interface AdminDeps {
  fetchTeams: () => Promise<{ ok: true; teams: TeamRow[] } | { ok: false; unauthorized?: boolean }>;
  fetchTeamDetail: (teamId: string) => Promise<{ ok: true; detail: TeamDetail } | { ok: false; unauthorized?: boolean }>;
  setTeamQuota: (teamId: string, limitBytes: number | null) => Promise<boolean>;
  revokeShare: (shareId: string) => Promise<boolean>;
  deactivateUser: (userId: string, deactivated: boolean) => Promise<boolean>;
  removeMember: (teamId: string, userId: string) => Promise<boolean>;
}
function fmtMb(bytes: number | null): string { return bytes == null ? t('common.unknown') : `${(bytes / (1024 * 1024)).toFixed(1)} ${t('common.mb')}`; }

export function mountAdminView(host: HTMLElement, opts: { deps: AdminDeps }): void {
  host.innerHTML = '';
  const root = document.createElement('div'); root.className = 'admin-view'; host.appendChild(root);

  function renderNotAuthorized(): void {
    root.innerHTML = '';
    const h = document.createElement('h1'); h.textContent = t('admin.title'); root.appendChild(h);
    const p = document.createElement('p'); p.dataset.role = 'not-authorized'; p.textContent = t('admin.notAuthorized'); root.appendChild(p);
    const a = document.createElement('a'); a.href = '/login'; a.textContent = t('admin.signIn'); root.appendChild(a);
  }
  function renderLoadError(): void {
    root.innerHTML = '';
    const h = document.createElement('h1'); h.textContent = t('admin.title'); root.appendChild(h);
    const p = document.createElement('p'); p.dataset.role = 'load-error'; p.textContent = t('admin.loadError'); root.appendChild(p);
    const btn = document.createElement('button'); btn.dataset.role = 'retry'; btn.textContent = t('admin.retry'); root.appendChild(btn);
    btn.addEventListener('click', () => { void load(); });
  }
  function showMutationError(): void {
    let p = root.querySelector('[data-role="mutation-error"]') as HTMLParagraphElement | null;
    if (!p) { p = document.createElement('p'); p.dataset.role = 'mutation-error'; root.insertBefore(p, root.firstChild); }
    p.textContent = t('admin.actionFailed');
  }
  async function load(): Promise<void> {
    const res = await opts.deps.fetchTeams();
    if (!res.ok) { res.unauthorized ? renderNotAuthorized() : renderLoadError(); return; }
    renderList(res.teams);
  }
  // ── List view: clickable team rows → drill into the team screen ──
  function renderList(teams: TeamRow[]): void {
    root.innerHTML = '';
    const h = document.createElement('h1'); h.textContent = t('admin.titleTeams'); root.appendChild(h);
    for (const team of teams) {
      const row = document.createElement('button'); row.dataset.role = 'team-row'; row.dataset.teamId = team.teamId; row.className = 'admin-row team-link';
      row.textContent = t('admin.teamRow', { name: team.name, owner: team.ownerEmail ?? '—', used: fmtMb(team.storageUsedBytes), limit: fmtMb(team.storageLimitBytes), members: t('admin.membersCount', { n: team.memberCount }) });
      row.addEventListener('click', () => { void openDetail(team.teamId); });
      root.appendChild(row);
    }
  }
  // ── Team detail view: back + quota + members + shares ──
  async function openDetail(teamId: string): Promise<void> {
    const res = await opts.deps.fetchTeamDetail(teamId);
    if (!res.ok) { res.unauthorized ? renderNotAuthorized() : renderLoadError(); return; }
    renderDetail(res.detail);
  }
  function renderDetail(detail: TeamDetail): void {
    const teamId = detail.team.id;
    root.innerHTML = '';
    const back = document.createElement('button'); back.dataset.role = 'back'; back.textContent = t('admin.back'); back.addEventListener('click', () => { void load(); }); root.appendChild(back);
    const h = document.createElement('h1'); h.textContent = detail.team.name; root.appendChild(h);
    // Quota
    const qh = document.createElement('h3'); qh.textContent = t('admin.storageQuota'); root.appendChild(qh);
    const qrow = document.createElement('div'); qrow.className = 'admin-row';
    const ql = document.createElement('span'); ql.className = 'admin-cell-main admin-num'; ql.textContent = `${fmtMb(detail.storageUsedBytes)} / ${fmtMb(detail.storageLimitBytes)}`; qrow.appendChild(ql);
    const qin = document.createElement('input'); qin.dataset.role = 'quota-mb'; qin.type = 'number'; qin.placeholder = t('common.mb'); qrow.appendChild(qin);
    const qbtn = document.createElement('button'); qbtn.dataset.role = 'set-quota'; qbtn.textContent = t('admin.setQuota'); qrow.appendChild(qbtn);
    qbtn.addEventListener('click', async () => {
      const mb = Number(qin.value);
      const limitBytes = Number.isFinite(mb) && mb > 0 ? Math.floor(mb * 1024 * 1024) : null;
      const ok = await opts.deps.setTeamQuota(teamId, limitBytes);
      if (!ok) { showMutationError(); return; }
      await openDetail(teamId);
    });
    const bar = document.createElement('div'); bar.className = 'admin-bar';
    const fill = document.createElement('i');
    const used = detail.storageUsedBytes ?? 0; const lim = detail.storageLimitBytes ?? 0;
    fill.style.width = lim > 0 ? `${Math.min(100, Math.round((used / lim) * 100))}%` : '0%';
    bar.appendChild(fill); qrow.appendChild(bar);
    root.appendChild(qrow);
    // Members
    const mh = document.createElement('h3'); mh.textContent = t('admin.membersHeading'); root.appendChild(mh);
    if (detail.members.length === 0) { const p = document.createElement('p'); p.textContent = t('admin.noMembers'); root.appendChild(p); }
    for (const m of detail.members) {
      const mr = document.createElement('div'); mr.dataset.role = 'member-row';
      const ml = document.createElement('span'); ml.textContent = m.deactivated ? t('admin.memberRowDeactivated', { email: m.email, role: m.role }) : t('admin.memberRow', { email: m.email, role: m.role }); mr.appendChild(ml);
      const da = document.createElement('button'); da.dataset.role = 'deactivate'; da.dataset.userId = m.userId; da.textContent = m.deactivated ? t('admin.activate') : t('admin.deactivate'); mr.appendChild(da);
      da.addEventListener('click', async () => { const ok = await opts.deps.deactivateUser(m.userId, !m.deactivated); if (!ok) { showMutationError(); return; } await openDetail(teamId); });
      const rm = document.createElement('button'); rm.dataset.role = 'remove-member'; rm.dataset.teamId = teamId; rm.dataset.userId = m.userId; rm.textContent = t('admin.remove'); mr.appendChild(rm);
      rm.addEventListener('click', async () => { const ok = await opts.deps.removeMember(teamId, m.userId); if (!ok) { showMutationError(); return; } await openDetail(teamId); });
      root.appendChild(mr);
    }
    // Shares
    const sh = document.createElement('h3'); sh.textContent = t('admin.sharesHeading'); root.appendChild(sh);
    if (detail.shares.length === 0) { const p = document.createElement('p'); p.textContent = t('admin.noShares'); root.appendChild(p); }
    for (const s of detail.shares) {
      const sr = document.createElement('div'); sr.dataset.role = 'share-row';
      const sl = document.createElement('span'); sl.textContent = t('admin.shareRow', { name: s.songName ?? t('library.untitled'), size: fmtMb(s.sizeBytes) }); sr.appendChild(sl);
      const rv = document.createElement('button'); rv.dataset.role = 'revoke-share'; rv.dataset.shareId = s.id; rv.textContent = t('admin.revoke'); sr.appendChild(rv);
      rv.addEventListener('click', async () => { const ok = await opts.deps.revokeShare(s.id); if (!ok) { showMutationError(); return; } await openDetail(teamId); });
      root.appendChild(sr);
    }
  }
  void load();
}
